import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import Errored from '../../core/Errored';
import { globalStyles } from '../../core/style/gstheme';
import { getGraphFromProject, makeCancelable } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import AssociatedProjectInfo from './AssociatedProjectInfo';
import AssociatedPropertyDisplay from './AssociatedPropertyDisplay';
import FindOntologyRelatedConceptDialog from './FindOntologyRelatedConceptDialog';

const styles = Object.assign({}, globalStyles);

class AssociatedProjectDisplay extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            pathList: null,
            pathLoading: true,
            monitorGuids: [],
            monitorIRIs: []
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.collectTemplateData();
    }

    static getDerivedStateFromProps(props, state) {
        if (props.resourceData._version !== state._version) {
            return {
                pathLoading: true,
                isLoading: false,
                _version: props.resourceData._version,
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.pathLoading === true) {
            this.collectTemplateData();
        }
    }

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph));
        return this.cancellable;
    }

    loadPathData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        this.unbindEvents();
    }

    unbindEvents() {
        this.state.monitorGuids.forEach((guid, index) => {
            const iri = this.state.monitorIRIs[index];
            GS.unbind(iri, this.props.graph, guid);
        });
    }

    handleGSevent = () => this.collectTemplateData();

    collectTemplateData() {
        if (this.state.isLoading) return;
        const { data } = this.props;
        this.unbindEvents();
        // We load ALL the template necessary at this level in the tree in one API call to avoid unnecessary calls
        this.setState({ isLoading: true }, () => {
            this.loadData(this.props.propertyTemplates)
                .promise
                .then(() => {
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    const associatedProjectGraph = getGraphFromProject(data.associatedProject);
                    const doesNotExist = this.props.propertyTemplates.filter(iri => !GS.isLoaded(iri, this.props.graph));
                    // Need to filter to only the associated templates for this associated project
                    let templates = this.props.propertyTemplates.filter(id => !doesNotExist.includes(id) && (!data.associatedTemplate || data.associatedTemplate.includes(id)));
                    // We have a slight difference in how concepts and labels are treated - for labels there will be a target class
                    // So we include the base inferred class as well in filtering
                    const applicableTemplates = templates.filter(template => {
                        const templateData = GS.data(template, this.props.graph);
                        if (!this.props.forceBaseClass) {
                            if (!templateData.shTargetClass || templateData.shTargetClass.length === 0) return true;
                        }
                        else {
                            if (!templateData.shTargetClass || templateData.shTargetClass.length === 0) return false;
                        }
                        // Shouldn't happen as Concept, Label or component class as always there
                        if (!this.props.resourceClasses || this.props.resourceClasses.length === 0) return false;
                        // We allow for subclassing        
                        let irClasses = [];
                        if (this.props.includeBaseClass || this.props.forceBaseClass) irClasses = [this.props.baseInferredClass];
                        const inferredResourceClasses = [].concat.apply(irClasses,
                            this.props.resourceClasses
                                .filter(classIRI => classIRI !== this.props.baseInferredClass)
                                .map(classIRI => GS.getSuperClasses(classIRI, associatedProjectGraph)));
                        return inferredResourceClasses.some(rc => templateData.shTargetClass.includes(rc));
                    });
                    // Now load property shapes
                    let propsList = [];
                    let newMonitorGuids = [];
                    let newMonitorIRIs = [];
                    applicableTemplates.sort().forEach(template => {
                        const templateData = GS.data(template, this.props.graph);
                        if ("shProperty" in templateData) {
                            propsList = propsList.concat(templateData.shProperty);
                        }
                        // We need to monitor for changes to templates
                        const monitorGuid = GS.bindChange(template, this.props.graph, this.handleGSevent);
                        newMonitorGuids.push(monitorGuid);
                        newMonitorIRIs.push(template);
                    });
                    this.setState({ monitorGuids: newMonitorGuids, monitorIRIs: newMonitorIRIs });
                    this.loadData(propsList)
                        .promise
                        .then(() => {
                            let pathList = [];
                            let pathInfo = new Map();
                            propsList.forEach(shProp => {
                                if (GS.isLoaded(shProp, this.props.graph)) {
                                    const shPropData = GS.data(shProp, this.props.graph);
                                    if (!pathList.includes(shPropData.shPath)
                                        && (!data.associatedProperty || !data.associatedProperty.includes(shPropData.shPath))) {
                                        pathList.push(shPropData.shPath);
                                    }
                                    const propInfo = pathInfo.has(shPropData.shPath) ? pathInfo.get(shPropData.shPath) : {};
                                    if (shPropData.shMinCount) {
                                        // In case multiple templates defining same prop take largest minCount
                                        if ("minCount" in propInfo && propInfo.minCount !== shPropData.shMinCount) propInfo.minCountConflict = true;
                                        if (!("minCount" in propInfo) || shPropData.shMinCount > propInfo.minCount) {
                                            propInfo.minCount = shPropData.shMinCount;
                                        }
                                    }
                                    if (shPropData.shMaxCount) {
                                        // In case multiple templates defining same prop take smallest maxCount
                                        if ("maxCount" in propInfo && propInfo.maxCount !== shPropData.shMaxCount) propInfo.maxCountConflict = true;
                                        if (!("maxCount" in propInfo) || shPropData.shMaxCount < propInfo.maxCount) {
                                            propInfo.maxCount = shPropData.shMaxCount;
                                        }
                                    }
                                    if (shPropData.shMaxLength) {
                                        // In case multiple templates defining same prop take smallest maxLength
                                        if (propInfo.maxLength && propInfo.maxLength !== shPropData.maxLength) propInfo.maxLengthConflict = true;
                                        if (!("maxLength" in propInfo) || shPropData.shMaxLength < propInfo.maxLength) {
                                            propInfo.maxLength = shPropData.shMaxLength;
                                        }
                                    }
                                    if ("inComponent" in shPropData && shPropData.inComponent.length > 0) {
                                        if (propInfo.inComponent && propInfo.inComponent !== shPropData.inComponent[0]) propInfo.inComponentConflict = true;
                                        propInfo.inComponent = shPropData.inComponent[0];
                                    }
                                    if ("shPattern" in shPropData && shPropData.shPattern !== "") {
                                        if (propInfo.pattern && propInfo.pattern !== shPropData.pattern) propInfo.patternConflict = true;
                                        propInfo.pattern = shPropData.shPattern;
                                    }
                                    if ("shName" in shPropData && shPropData.shName && shPropData.shName[this.props.defaultLang]) {
                                        const nameValue = Array.isArray(shPropData.shName[this.props.defaultLang]) ?
                                            shPropData.shName[this.props.defaultLang][0]
                                            :
                                            shPropData.shName[this.props.defaultLang];
                                        if (!propInfo.name || nameValue > propInfo.name) {
                                            propInfo.name = nameValue;
                                        }
                                    }
                                    if ("shDescription" in shPropData && shPropData.shDescription && shPropData.shDescription[this.props.defaultLang]) {
                                        const descValue = Array.isArray(shPropData.shDescription[this.props.defaultLang]) ?
                                            shPropData.shDescription[this.props.defaultLang][0]
                                            :
                                            shPropData.shDescription[this.props.defaultLang];
                                        if (!propInfo.description || descValue > propInfo.description) {
                                            propInfo.description = descValue;
                                        }
                                    }
                                    pathInfo.set(shPropData.shPath, propInfo);
                                    // We need to monitor for changes to property shapes
                                    const monitorGuid = GS.bindChange(shProp, this.props.graph, this.handleGSevent);
                                    newMonitorGuids.push(monitorGuid);
                                    newMonitorIRIs.push(shProp);
                                }
                            });
                            // Need to sort the props
                            this.loadPathData(pathList, associatedProjectGraph)
                                .promise
                                .then(() => {
                                    pathList.sort((a, b) => {
                                        const aData = GS.isLoaded(a, associatedProjectGraph) ? GS.data(a, associatedProjectGraph) : null;
                                        const bData = GS.isLoaded(b, associatedProjectGraph) ? GS.data(b, associatedProjectGraph) : null;
                                        const labelA = aData && aData.label[this.props.defaultLang] ? aData.label[this.props.defaultLang] : a;
                                        const labelB = bData && bData.label[this.props.defaultLang] ? bData.label[this.props.defaultLang] : b;
                                        if (labelA < labelB) return -1;
                                        return 1;
                                    });
                                    if (this.cancellable && !this.cancellable.isCancelled()) {
                                        this.setState({ isLoading: false, pathList, pathInfo, pathLoading: false });
                                    }
                                })
                                .catch((e) => {
                                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, isLoading: false });
                                });
                        })
                        .catch((e) => {
                            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, isLoading: false });
                        });
                })
                .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, isLoading: false });
                });
        });
    }

    render() {

        const { isReadOnly, data, defaultLang, components, onDelete, onUpdate, projectLang, linkedData,
            includeExistingResource, addNewLiteral, addNewTypedLiteral, viewLang, graph, associatedTaxonomyProject,
            resourceClasses, resourceData, projectIRI, schemeIRI, useWorkflow, dialogComponent } = this.props;
        const { pathList, pathInfo, isErrored } = this.state;
        if (isErrored) return <Errored data={isErrored} />;

        const associatedProjectGraph = getGraphFromProject(data.associatedProject);
        const areGlobalProps = data.associatedProperty ? data.associatedProperty.length > 0 : false;

        return (
            <React.Fragment>
                {((data.associatedProperty && data.associatedProperty.length > 0)
                    || (pathList && pathList.length > 0)) &&
                    <Grid data-test="global-props" item xs={12}>
                        <AssociatedProjectInfo
                            graph={associatedProjectGraph}
                            id={data.associatedProject}
                            includeHeading={areGlobalProps}
                            defaultLang={defaultLang}
                            handleNotAvailable={true} />
                    </Grid>
                }
                {data.associatedProperty && data.associatedProperty.length > 0 &&
                    [...data.associatedProperty].sort().map(ap =>
                        <AssociatedPropertyDisplay
                            dialog={dialogComponent}
                            key={ap}
                            resourceData={resourceData}
                            resourceClasses={resourceClasses}
                            viewLang={viewLang}
                            useWorkflow={useWorkflow}
                            linkedData={linkedData}
                            projectLang={projectLang}
                            projectIRI={projectIRI}
                            addNewLiteral={addNewLiteral}
                            addNewTypedLiteral={addNewTypedLiteral}
                            associatedProject={associatedTaxonomyProject}
                            includeExistingResource={includeExistingResource}
                            components={components}
                            isReadOnly={isReadOnly}
                            schemeIRI={schemeIRI}
                            onAssociatedPropertyDelete={onDelete}
                            onAssociatedPropertyUpdate={onUpdate}
                            id={ap}
                            handleNotAvailable={true}
                            graph={associatedProjectGraph}
                            projectGraph={graph}
                            associatedProjectGraph={associatedProjectGraph}
                            defaultLang={defaultLang} />
                    )
                }
                {pathList && pathList.length > 0 &&
                    <React.Fragment>
                        <Grid data-test="template-props" item xs={12}>
                            <div><Typography variant="caption" style={{ marginTop: 6 }}>Template</Typography></div>
                        </Grid>
                        {pathList.map(ap =>
                            <AssociatedPropertyDisplay
                                dialog={dialogComponent}
                                key={ap}
                                info={pathInfo.get(ap)}
                                resourceData={resourceData}
                                resourceClasses={resourceClasses}
                                viewLang={viewLang}
                                useWorkflow={useWorkflow}
                                projectLang={projectLang}
                                projectIRI={projectIRI}
                                linkedData={linkedData}
                                associatedProject={associatedTaxonomyProject}
                                addNewLiteral={addNewLiteral}
                                addNewTypedLiteral={addNewTypedLiteral}
                                includeExistingResource={includeExistingResource}
                                components={components}
                                isReadOnly={isReadOnly}
                                schemeIRI={schemeIRI}
                                onAssociatedPropertyDelete={onDelete}
                                onAssociatedPropertyUpdate={onUpdate}
                                id={ap}
                                handleNotAvailable={true}
                                graph={associatedProjectGraph}
                                projectGraph={graph}
                                associatedProjectGraph={associatedProjectGraph}
                                defaultLang={defaultLang} />
                        )}
                    </React.Fragment>
                }
            </React.Fragment>
        );
    }
}

AssociatedProjectDisplay.defaultProps = {
    propertyTemplates: [],
    useWorkflow: false,
    baseInferredClass: "http://www.w3.org/2004/02/skos/core#Concept",
    includeBaseClass: false,
    forceBaseClass: false,
    dialogComponent: FindOntologyRelatedConceptDialog
};

AssociatedProjectDisplay.propTypes = {
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    components: PropTypes.array,
    includeBaseClass: PropTypes.bool,
    forceBaseClass: PropTypes.bool,
    isReadOnly: PropTypes.bool.isRequired,
    projectIRI: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    viewLang: PropTypes.string,
    projectLang: PropTypes.array.isRequired,
    addNewLiteral: PropTypes.func.isRequired,
    propertyTemplates: PropTypes.array,
    linkedData: PropTypes.array,
    associatedTaxonomyProject: PropTypes.any,
    addNewTypedLiteral: PropTypes.func.isRequired,
    includeExistingResource: PropTypes.func,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    schemeIRI: PropTypes.string,
    resourceData: PropTypes.object.isRequired,
    resourceClasses: PropTypes.array.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(AssociatedProjectDisplay));